import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, SHAPES } from '../gameData';

export default class ChairArea extends RegionArea
{
    /**
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[parentRegion.region].CHAIR);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.leftArmRest.setAlpha(0);
        this.rightArmRest.setAlpha(0);
        this.seat.setAlpha(0);
        this.seatBack.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.leftArmRest.disableInteractive();
        this.rightArmRest.disableInteractive();
        this.seat.disableInteractive();
        this.seatBack.disableInteractive();
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.leftArmRest.setAlpha(0.000001);
        this.rightArmRest.setAlpha(0.000001);
        this.seat.setAlpha(0.000001);
        this.seatBack.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        let chairArmHitArea = new Phaser.Geom.Polygon(
            [
                { x: 46, y: 130 },
                { x: 0, y: 101 },
                { x: 173, y: 0 },
                { x: 220, y: 28 },
            ]);
        this.leftArmRest = ABCya.Images.addImage(808, 412, UIAssetKeys._10_Camping_Zoom_In_Chair_Slct_Arm, this.parentRegion).setInteractive(chairArmHitArea, Phaser.Geom.Polygon.Contains);
        this.leftArmRest.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.leftArmRest, SHAPES.RECTANGLE);
        });

        this.rightArmRest = ABCya.Images.addImage(596, 289, UIAssetKeys._10_Camping_Zoom_In_Chair_Slct_Arm, this.parentRegion).setInteractive(chairArmHitArea, Phaser.Geom.Polygon.Contains);
        this.rightArmRest.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.rightArmRest, SHAPES.RECTANGLE);
        });

        let seatHitArea = new Phaser.Geom.Polygon(
            [
                { x: 174, y: 204 },
                { x: 0, y: 102 },
                { x: 174, y: 0 },
                { x: 348, y: 102 },
            ]);
        this.seat = ABCya.Images.addImage(702, 427, UIAssetKeys._10_Camping_Zoom_In_Chair_Slct_St, this.parentRegion).setInteractive(seatHitArea, Phaser.Geom.Polygon.Contains);
        this.seat.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.seat, SHAPES.SQUARE);
        });

        let seatBackHitArea = new Phaser.Geom.Polygon(
            [
                { x: 174, y: 318 },
                { x: 0, y: 216 },
                { x: 50, y: 0 },
                { x: 225, y: 100 },
            ]);
        this.seatBack = ABCya.Images.addImage(810, 272, UIAssetKeys._10_Camping_Zoom_In_Chair_Slct_Bk, this.parentRegion).setInteractive(seatBackHitArea, Phaser.Geom.Polygon.Contains);
        this.seatBack.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.seatBack, SHAPES.SQUARE);
        });

        this.shapeOverlays[SHAPES.RECTANGLE] = [this.leftArmRest, this.rightArmRest];
        this.shapeOverlays[SHAPES.SQUARE] = [this.seat, this.seatBack];

        this._createAreaInformation(0x5f5fd6, 0x204243);
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.tent, this.parentRegion.treeAreaContainer, this.parentRegion.truck
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.chair, duration: 1000, props: { scale: 1, x: 710, y: 383 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 }
        );

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.leftArmRest.disableInteractive();
        this.rightArmRest.disableInteractive();
        this.seat.disableInteractive();
        this.seatBack.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.chair, duration: 1000, props: { scale: 0.2027, x: this.parentRegion.chair.onScreenPosition.x, y: this.parentRegion.chair.onScreenPosition.y }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.tent, this.parentRegion.treeAreaContainer, this.parentRegion.truck], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
        );

        return tweens;
    }
}